'use client';
import React, { useEffect, useState } from 'react';
import {
  Users,
  CalendarCheck,
  AlertCircle,
  TrendingUp,
  Clock,
  IndianRupee,
  UserPlus,
  XCircle,
} from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

interface KPIData {
  activeMembers: number;
  todayCheckIns: number;
  outstandingDues: number;
  renewalsDue: number;
  monthlyRevenue: number;
  newRegistrations: number;
  noShowRate: number;
  sportBreakdown: { label: string; count: number }[];
}

function formatINR(amount: number): string {
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
  return `₹${amount}`;
}

function getTodayStr(): string {
  const d = new Date();
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function getMonthStr(): string {
  const d = new Date();
  return `${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function getWeekAgoStr(): string {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

export default function KPIBentoGrid() {
  const [kpi, setKpi] = useState<KPIData>({
    activeMembers: 0,
    todayCheckIns: 0,
    outstandingDues: 0,
    renewalsDue: 0,
    monthlyRevenue: 0,
    newRegistrations: 0,
    noShowRate: 0,
    sportBreakdown: [],
  });

  useEffect(() => {
    async function load() {
      try {
        const supabase = createClient();
        const today = getTodayStr();
        const weekAgo = getWeekAgoStr();
        const monthStr = getMonthStr(); // mm/yyyy

        const [membersRes, smRes, checkInsRes, bookingsRes, paymentsRes] = await Promise.all([
          supabase.from('members').select('membership_status, dues, join_date, sports, id'),
          supabase.from('sport_memberships').select('sport, status, expiry_date, member_id'),
          supabase.from('attendance').select('status, date').eq('date', today),
          supabase.from('bookings').select('status, date').eq('date', today),
          supabase.from('payments').select('paid_amount, amount, status, date'),
        ]);

        const members = membersRes.data ?? [];
        const sportMemberships = smRes.data ?? [];
        const checkIns = checkInsRes.data ?? [];
        const bookings = bookingsRes.data ?? [];
        const payments = paymentsRes.data ?? [];

        // Resolve effective membership status using same logic as dbRowToMember
        const resolvedMembers = members.map((m: any) => {
          const storedStatus = m.membership_status ?? 'prospect';
          const memberSportMemberships = sportMemberships.filter((sm: any) => sm.member_id === m.id);
          const hasActiveSportMembership = memberSportMemberships.some(
            (sm: any) => (sm.status ?? 'active') === 'active'
          );
          const membershipStatus =
            storedStatus === 'prospect' && hasActiveSportMembership ? 'active' : storedStatus;
          return { ...m, membershipStatus };
        });

        // Active members — matches MemberManagementPage activeCount
        const activeMembers = resolvedMembers.filter((m) => m.membershipStatus === 'active').length;

        // Today check-ins
        const todayCheckIns = checkIns.length;

        // Outstanding dues from members table
        const outstandingDues = members.reduce((s: number, m: any) => s + (m.dues ?? 0), 0);

        // Renewals due — members with 'expiring' status (matches MemberManagementPage expiringCount)
        const renewalsDue = resolvedMembers.filter((m) => m.membershipStatus === 'expiring').length;

        // Monthly revenue
        const monthlyRevenue = payments
          .filter((p) => {
            if (!p.date || p.date === '-') return false;
            const parts = p.date.split('/');
            if (parts.length !== 3) return false;
            return `${parts[1]}/${parts[2]}` === monthStr;
          })
          .reduce((s: number, p: any) => s + (p.paid_amount ?? 0), 0);

        // New registrations this week
        const newRegistrations = members.filter((m) => {
          if (!m.join_date) return false;
          const [dd, mm, yy] = m.join_date.split('/').map(Number);
          if (!dd || !mm || !yy) return false;
          const joined = new Date(yy, mm - 1, dd);
          const [wdd, wmm, wyy] = weekAgo.split('/').map(Number);
          const week = new Date(wyy, wmm - 1, wdd);
          return joined >= week;
        }).length;

        // No-show rate from today's bookings
        const totalBookings = bookings.length;
        const noShows = bookings.filter((b) => b.status === 'no-show').length;
        const noShowRate = totalBookings > 0 ? parseFloat(((noShows / totalBookings) * 100).toFixed(1)) : 0;

        // Sport breakdown from sport_memberships
        const sportCounts: Record<string, number> = {};
        sportMemberships.filter((sm) => sm.status === 'active').forEach((sm) => {
          if (sm.sport) sportCounts[sm.sport] = (sportCounts[sm.sport] ?? 0) + 1;
        });
        const sportBreakdown = Object.entries(sportCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([label, count]) => ({ label, count }));

        setKpi({ activeMembers, todayCheckIns, outstandingDues, renewalsDue, monthlyRevenue, newRegistrations, noShowRate, sportBreakdown });
      } catch (e) {
        console.error('KPI load error:', e);
      }
    }
    load();
  }, []);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4 auto-rows-auto">

      {/* Hero: Active Members — col-span-2 row-span-2 */}
      <div className="col-span-2 row-span-2 bg-primary rounded-xl p-6 flex flex-col justify-between min-h-[160px] relative overflow-hidden">
        <div className="absolute right-0 bottom-0 opacity-10">
          <Users size={120} />
        </div>
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-2">
            <p className="text-[12px] font-semibold uppercase tracking-widest text-white/70">Active Members</p>
            <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
              <Users size={16} className="text-white" />
            </div>
          </div>
          <p className="text-5xl font-bold text-white tabular-nums mt-4">
            {kpi.activeMembers.toLocaleString('en-IN')}
          </p>
          <p className="text-white/70 text-[13px] mt-1">Total members with active memberships</p>
        </div>
        <div className="relative z-10 flex items-center gap-4 flex-wrap">
          {kpi.sportBreakdown.length > 0 ? kpi.sportBreakdown.map((s) => (
            <div key={`sport-kpi-${s.label}`} className="bg-white/10 rounded-lg px-3 py-1.5">
              <p className="text-white/60 text-[10px] uppercase tracking-wide">{s.label}</p>
              <p className="text-white font-bold text-[14px] tabular-nums">{s.count}</p>
            </div>
          )) : (
            <p className="text-white/50 text-[12px]">No sport data yet</p>
          )}
        </div>
      </div>

      {/* Today's Check-ins */}
      <div className="bg-card border border-border rounded-xl p-4 flex flex-col justify-between">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Today's Check-ins</p>
          <div className="w-7 h-7 rounded-lg bg-success-bg flex items-center justify-center">
            <CalendarCheck size={14} className="text-success" />
          </div>
        </div>
        <p className="text-3xl font-bold text-foreground tabular-nums">{kpi.todayCheckIns}</p>
        <div className="flex items-center gap-1 mt-2">
          <TrendingUp size={12} className="text-muted-foreground" />
          <p className="text-[11px] text-muted-foreground font-medium">Today's total</p>
        </div>
      </div>

      {/* Outstanding Dues — warning */}
      <div className="bg-danger-bg border border-danger/20 rounded-xl p-4 flex flex-col justify-between">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-danger/70">Outstanding Dues</p>
          <div className="w-7 h-7 rounded-lg bg-danger/10 flex items-center justify-center">
            <AlertCircle size={14} className="text-danger" />
          </div>
        </div>
        <p className="text-3xl font-bold text-danger tabular-nums">{formatINR(kpi.outstandingDues)}</p>
        <div className="flex items-center gap-1 mt-2">
          <p className="text-[11px] text-danger/70 font-medium">Across all members</p>
        </div>
      </div>

      {/* Renewals Due — warning */}
      <div className="bg-warning-bg border border-warning/20 rounded-xl p-4 flex flex-col justify-between">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-warning/80">Renewals Due</p>
          <div className="w-7 h-7 rounded-lg bg-warning/10 flex items-center justify-center">
            <Clock size={14} className="text-warning" />
          </div>
        </div>
        <p className="text-3xl font-bold text-warning tabular-nums">{kpi.renewalsDue}</p>
        <p className="text-[11px] text-warning/70 font-medium mt-2">Within next 7 days</p>
      </div>

      {/* No-Show Rate */}
      <div className="bg-card border border-border rounded-xl p-4 flex flex-col justify-between">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">No-Show Rate</p>
          <div className="w-7 h-7 rounded-lg bg-danger-bg flex items-center justify-center">
            <XCircle size={14} className="text-danger" />
          </div>
        </div>
        <p className="text-3xl font-bold text-foreground tabular-nums">{kpi.noShowRate}%</p>
        <p className="text-[11px] text-muted-foreground mt-2">Of today's bookings</p>
      </div>

      {/* Monthly Revenue — col-span-2 */}
      <div className="col-span-2 bg-card border border-border rounded-xl p-4 flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">Revenue This Month</p>
          <div className="w-7 h-7 rounded-lg bg-accent/10 flex items-center justify-center">
            <IndianRupee size={14} className="text-accent" />
          </div>
        </div>
        <div className="flex items-end justify-between">
          <div>
            <p className="text-3xl font-bold text-foreground tabular-nums">{formatINR(kpi.monthlyRevenue)}</p>
            <p className="text-[11px] text-muted-foreground mt-1">Collected this month</p>
          </div>
        </div>
      </div>

      {/* New Registrations */}
      <div className="bg-card border border-border rounded-xl p-4 flex flex-col justify-between">
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">New Members</p>
          <div className="w-7 h-7 rounded-lg bg-success-bg flex items-center justify-center">
            <UserPlus size={14} className="text-success" />
          </div>
        </div>
        <p className="text-3xl font-bold text-foreground tabular-nums">{kpi.newRegistrations}</p>
        <p className="text-[11px] text-muted-foreground mt-2">This week</p>
      </div>

    </div>
  );
}